import { cloneElement, useCallback, useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../authConfig.js";

/**
 * Wraps a route that requires Microsoft authentication + security group membership.
 * Redirects to /login if not authenticated.
 * Validates group membership via /api/validate-access on first load.
 */
export default function ProtectedRoute({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();

  const [authorized, setAuthorized] = useState(null); // null = checking, true/false = result
  const [isAdmin, setIsAdmin] = useState(false);
  const [uiSettings, setUiSettings] = useState({});
  const [error, setError] = useState(null);
  // Bump to re-run the validation effect. Children call refreshAuth() (exposed
  // below) after any settings save so new uiSettings / isAdmin propagate
  // without a browser reload.
  const [refreshToken, setRefreshToken] = useState(0);
  const refreshAuth = useCallback(() => setRefreshToken((t) => t + 1), []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    // Acquire token silently and validate group membership
    const account = accounts[0];
    if (!account) return;

    // On the first load we retry with a delay because the Functions host can
    // still be warming up. For a user-triggered refresh we skip the retry
    // storm — a single attempt is enough.
    const isRefresh = refreshToken > 0;

    const validateWithRetry = async (retries = isRefresh ? 0 : 3) => {
      try {
        const response = await instance.acquireTokenSilent({ ...loginRequest, account });
        // Send the ID token (audience = our app) so the backend can verify it.
        // SWA overwrites `Authorization` with its own internal token, so the ID
        // token rides in X-Booking-Auth (forwarded untouched); Authorization is
        // kept for local dev / direct API access.
        const res = await fetch(`/api/validate-access`, {
          headers: {
            "X-Booking-Auth": `Bearer ${response.idToken}`,
            Authorization: `Bearer ${response.idToken}`,
          },
        });

        // Handle non-JSON responses gracefully
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); }
        catch { data = { authorized: false, error: text || `HTTP ${res.status}` }; }

        if (data.authorized) {
          setAuthorized(true);
          setIsAdmin(Boolean(data.isAdmin));
          setUiSettings(data.uiSettings || {});
        } else {
          setAuthorized(false);
          setError(data.error || "Access denied");
        }
      } catch (err) {
        console.error(`[ProtectedRoute] Auth validation failed (retries left: ${retries}):`, err);
        if (retries > 0) {
          // Retry after a delay (API may still be starting)
          await new Promise((r) => setTimeout(r, 2000));
          return validateWithRetry(retries - 1);
        }
        // On a refresh (not the initial load) don't boot the user to Access
        // Denied on transient errors — just log and keep the existing state.
        if (!isRefresh) {
          setAuthorized(false);
          setError(err.message || "Authentication failed");
        }
      }
    };

    validateWithRetry();
  }, [isAuthenticated, accounts, instance, navigate, refreshToken]);

  if (!isAuthenticated || authorized === null) {
    return (
      <div style={{ minHeight: "100vh", background: "#F5FAFA", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,'Segoe UI',system-ui,sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 32, height: 32, border: "3px solid #E2E8F0", borderTopColor: "#004e42", borderRadius: "50%", animation: "spin .75s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: "#64748B", fontSize: 14 }}>Verifying access...</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (authorized === false) {
    return (
      <div style={{ minHeight: "100vh", background: "#F5FAFA", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,'Segoe UI',system-ui,sans-serif" }}>
        <div style={{ background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 16, padding: "40px 48px", textAlign: "center", maxWidth: 420 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <svg width="24" height="24" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#EF4444" strokeWidth="1.3"/><path d="M5.75 5.75l4.5 4.5M10.25 5.75l-4.5 4.5" stroke="#EF4444" strokeWidth="1.3" strokeLinecap="round"/></svg>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", margin: "0 0 8px" }}>Access Denied</h2>
          <p style={{ fontSize: 14, color: "#64748B", margin: "0 0 24px", lineHeight: 1.6 }}>{error}</p>
          <button
            onClick={() => navigate("/")}
            style={{ padding: "10px 24px", borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#fff", color: "#374151", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            Back to Booking
          </button>
        </div>
      </div>
    );
  }

  return cloneElement(children, { isAdmin, uiSettings, refreshAuth });
}
